import { NgModule } from '@angular/core';
import { Routes, RouterModule, RouterStateSnapshot } from '@angular/router';
import { PortalComponent } from './portal.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { PortalHomeComponent } from './pages/portal-home/portal-home.component';
import { MaintenanceModeComponent } from 'src/app/maintenance-mode/maintenance-mode.component';
import { PortalLoginComponent } from './pages/portal-login/portal-login.component';
import { isAuthenticated } from './auth/auth.guard';
import { ModuleGuardService } from 'src/app/guards/module-guard.service';
import { TenantModuleType } from 'src/app/shared/models/tenantModuleType';
import { PageNotFoundComponent } from 'src/app/shared/components/page-not-found/page-not-found.component';
import { CredentialsComponent } from './pages/credentials/credentials.component';
import { PortalTeamsComponent } from './pages/portal-teams/portal-teams.component';
import { PortalProfileComponent } from './pages/portal-profile/portal-profile.component';
import { PortalFormComponent } from './pages/portal-form/portal-form.component';
import { MessagesComponent } from './pages/messages/messages.component';
import { PaymentServiceResolver } from 'src/shared-app-portal/services/payment.service';
import { PortalPaymentsComponent } from './pages/payment/portal-payments.component';
import { InvalidApplicationComponent } from 'src/shared-app-portal/components/lineup-applications/invalid-application/invalid-application.component';
import { ApplicationClosedComponent } from 'src/shared-app-portal/components/lineup-applications/application-closed/application-closed.component';
import { ApplicationRequestComponent } from 'src/app/members/application-request/application-request.component';
import { ApplicationComponent } from 'src/shared-app-portal/components/lineup-applications/application/application.component';
import { SurveyComponent } from 'src/shared-app-portal/components/lineup-applications/survey/survey.component';
import { SurveyMode } from 'src/shared-app-portal/components/lineup-applications/survey/surveyMode';
import { SurveyIntroductionComponent } from 'src/shared-app-portal/components/lineup-applications/survey/survey-introduction/survey-introduction.component';
import { SurveyRespondComponent } from 'src/shared-app-portal/components/lineup-applications/survey/survey-respond/survey-respond.component';
import { SurveyExpiredComponent } from 'src/shared-app-portal/components/lineup-applications/survey/survey-expired/survey-expired.component';
import { SurveyConfirmationComponent } from 'src/shared-app-portal/components/lineup-applications/survey/survey-confirmation/survey-confirmation.component';
import { EvaluationComponent } from 'src/shared-app-portal/components/lineup-applications/evaluation/evaluation.component';
import { EvaluationMode } from 'src/shared-app-portal/components/lineup-applications/evaluation/models/evaluationMode';
import { EvaluationRespondComponent } from 'src/shared-app-portal/components/lineup-applications/evaluation/evaluation-respond/evaluation-respond.component';
import { EvaluationIntroductionComponent } from 'src/shared-app-portal/components/lineup-applications/evaluation/evaluation-introduction/evaluation-introduction.component';
import { EvaluationExpiredComponent } from 'src/shared-app-portal/components/lineup-applications/evaluation/evaluation-expired/evaluation-expired.component';
import { EvaluationConfirmationComponent } from 'src/shared-app-portal/components/lineup-applications/evaluation/evaluation-confirmation/evaluation-confirmation.component';
import { InvitationComponent } from 'src/shared-app-portal/components/lineup-applications/invitation/invitation.component';
import { ApplicationConfirmationComponent } from 'src/shared-app-portal/components/lineup-applications/application/application-confirmation/application-confirmation.component';
import { EmailPreferencesComponent } from 'src/shared-app-portal/components/lineup-applications/email-preferences/email-preferences.component';
import { SecureLinkGuardService as SecureLinkGuard } from 'src/shared-app-portal/services/secure-link-guard.service';
import { PermissionDeniedComponent } from 'src/app/shared/components/permission-denied/permission-denied.component';
import { PortalTeamManagerMainPageComponent } from './pages/portal-team-manager-pages/portal-team-manager-main-page.component';
import { PortalTeamCredentialsComponent } from './pages/portal-team-manager-pages/portal-team-credentials/portal-team-credentials.component';
import { PortalTeamPaymentsComponent } from './pages/portal-team-manager-pages/portal-team-payments/portal-team-payments.component';

const isAuthenticatedGuard = (route: any, state: RouterStateSnapshot) => isAuthenticated(state);

const routes: Routes = [
  { path: '', redirectTo: 'portal', pathMatch: 'full' },
  {
    path: 'portal',
    component: PortalComponent,
    canActivate: [ModuleGuardService],
    data: { moduleType: TenantModuleType.Portal },
    children: [
      {
        path: '',
        component: PortalHomeComponent,
        canActivateChild: [isAuthenticatedGuard],
        children: [
          {
            path: '',
            redirectTo: 'dashboard',
            pathMatch: 'full'
          },
          {
            path: 'dashboard',
            component: DashboardComponent,
            data: { title: 'Dashboard | ${tenant} Portal' }
          },
          {
            path: 'credentials',
            component: CredentialsComponent,
            data: { title: 'Credentials | ${tenant} Portal' }
          },
          {
            path: 'profile',
            component: PortalProfileComponent,
            data: { title: 'Profile | ${tenant} Portal' }
          },
          {
            path: 'teams',
            component: PortalTeamsComponent,
            data: { title: 'Teams | ${tenant} Portal' }
          },
          {
            path: 'messages',
            component: MessagesComponent,
            data: { title: 'Messages | ${tenant} Portal' }
          },
          {
            path: 'payments',
            component: PortalPaymentsComponent,
            data: { title: 'Payments | ${tenant} Portal' },
            resolve: { paymentService: PaymentServiceResolver }
          },
          {
            path: 'submitted-forms',
            component: PortalFormComponent,
            data: { title: 'Submitted Forms | ${tenant} Portal' }
          },
          {
            path: 'forms',
            children: [
              {
                path: 'application/invalid',
                component: InvalidApplicationComponent,
                data: { title: '${application} | ${tenant} Portal', isPortalForm: true }
              },
              {
                path: 'application/closed',
                component: ApplicationClosedComponent,
                data: { title: '${application} | ${tenant} Portal', isPortalForm: true }
              },
              {
                path: 'application/:formId/request',
                component: ApplicationRequestComponent,
                data: { title: '${application} | ${tenant} Portal' }
              },
              {
                path: 'application/:secureLinkId',
                component: ApplicationComponent,
                canActivate: [SecureLinkGuard],
                data: {
                  target: 'portal/forms/application/invalid',
                  title: '${application} | ${tenant} Portal',
                  isPortalForm: true,
                  navigationPath: 'portal/forms'
                }
              },
              {
                path: 'survey/:secureLinkId',
                component: SurveyComponent,
                children: [
                  {
                    path: SurveyMode.Introduction,
                    component: SurveyIntroductionComponent,
                    data: { title: '${survey} | ${tenant} Portal' }
                  },
                  {
                    path: SurveyMode.Respond,
                    component: SurveyRespondComponent,
                    data: { title: '${survey} | ${tenant} Portal' }
                  },
                  {
                    path: SurveyMode.Expired,
                    component: SurveyExpiredComponent,
                    data: { title: '${survey} | ${tenant} Portal', isPortalForm: true }
                  },
                  {
                    path: SurveyMode.Confirmation,
                    component: SurveyConfirmationComponent,
                    data: { title: '${survey} | ${tenant} Portal', isPortalForm: true }
                  }
                ]
              },
              {
                path: 'evaluation/:secureLinkId',
                component: EvaluationComponent,
                children: [
                  {
                    path: EvaluationMode.Introduction,
                    component: EvaluationIntroductionComponent,
                    data: { title: '${evaluation} | ${tenant} Portal' }
                  },
                  {
                    path: EvaluationMode.Respond,
                    component: EvaluationRespondComponent,
                    data: { title: '${evaluation} | ${tenant} Portal' }
                  },
                  {
                    path: EvaluationMode.Expired,
                    component: EvaluationExpiredComponent,
                    data: { title: '${evaluation} | ${tenant} Portal', isPortalForm: true }
                  },
                  {
                    path: EvaluationMode.Confirmation,
                    component: EvaluationConfirmationComponent,
                    data: { title: '${evaluation} | ${tenant} Portal', isPortalForm: true }
                  }
                ]
              },
              {
                path: ':personId/seat/:seatId',
                component: InvitationComponent,
                data: { title: '${team} invitation for ${person} | ${tenant} Portal' }
              },
              {
                path: ':personId/application/:secureLinkId',
                component: ApplicationComponent,
                canActivate: [SecureLinkGuard],
                data: {
                  target: 'portal/forms/application/invalid',
                  title: '${application} | ${tenant} Portal',
                  isPortalForm: true,
                  navigationPath: 'portal/forms'
                }
              },
              {
                path: 'confirmation',
                component: ApplicationConfirmationComponent,
                data: { title: '${application} | ${tenant} Portal', isPortalForm: true }
              },
              {
                path: ':personId/emailPreferences',
                component: EmailPreferencesComponent,
                data: { title: 'Email Preferences | ${tenant} Portal' }
              }
            ]
          },
          {
            path: 'not-found',
            component: PageNotFoundComponent,
            data: { showToolbar: false, title: 'Page Not Found | ${tenant} Portal' }
          },
          {
            path: 'permission-denied',
            component: PermissionDeniedComponent,
            data: { showToolbar: false, title: 'Permission Denied | ${tenant} Portal' }
          },
          {
            path: 'team-management',
            component: PortalTeamManagerMainPageComponent,
            data: { title: 'Team Management | ${tenant} Portal' },
            children: [
              {
                path: ':id/credentials',
                component: PortalTeamCredentialsComponent,
                data: { title: 'Team Credentials | ${tenant} Portal' }
              },
              {
                path: ':id/payments',
                component: PortalTeamPaymentsComponent,
                data: { title: 'Team Payments | ${tenant} Portal' }
              }
            ]
          }
        ]
      },
      {
        path: 'login',
        component: PortalLoginComponent,
        data: { title: 'Login | ${tenant} Portal' }
      }
    ]
  },
  { path: 'maintenance-mode', component: MaintenanceModeComponent },
  { path: 'not-found', component: PageNotFoundComponent, data: { showToolbar: false } },
  { path: '**', redirectTo: 'portal/not-found', pathMatch: 'full' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class PortalRoutingModule {}
